/**
 * App auth client — mapper sign-in + Google SSO.
 *
 * These talk to the backend `/api/auth/*` endpoints and power the app's login
 * gate (LoginGate). They were previously colocated with the (now-removed) LLM
 * Bedrock client; they are plain auth calls with no AI dependency.
 */

import { type Mapper } from '../store/authStore';
import { API_BASE } from './apiBase';

async function extractError(res: Response): Promise<string> {
  // FastAPI HTTPException → {detail: "..."}; our global middleware → {error, type};
  // anything else → fall back to raw text or status code.
  const raw = await res.text().catch(() => '');
  if (!raw) return `HTTP ${res.status}`;
  try {
    const json = JSON.parse(raw);
    const msg = json.detail ?? json.error ?? json.message;
    if (msg) return typeof msg === 'string' ? msg : JSON.stringify(msg);
  } catch {
    /* not JSON */
  }
  return raw.slice(0, 400) || `HTTP ${res.status}`;
}

export async function loginMapper(
  username: string, password: string,
): Promise<{ mapper: Mapper; token: string }> {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  const body = await res.json();
  return { mapper: { id: body.id, display_name: body.display_name, role: body.role, apps: body.apps }, token: body.token };
}

/** Request a one-time login code by email. The backend always returns a generic
 *  message and never reveals whether the email is authorized. */
export async function requestOtp(email: string): Promise<{ ok: boolean; message: string }> {
  const res = await fetch(`${API_BASE}/api/auth/otp/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

/** Verify an emailed one-time code. Returns the same session shape as Google
 *  sign-in on success; throws with the server detail on 401 (bad/expired) or
 *  429 (too many attempts). */
export async function verifyOtp(
  email: string, code: string,
): Promise<{ mapper: Mapper; token: string; email: string }> {
  const res = await fetch(`${API_BASE}/api/auth/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  const body = await res.json();
  return {
    mapper: { id: body.id, display_name: body.display_name, role: body.role, apps: body.apps },
    token: body.token,
    email: body.email,
  };
}

/** The current session's account info (email, role, admin flag). */
export async function getMe(): Promise<{ email: string; role: string; is_admin: boolean }> {
  const res = await fetch(`${API_BASE}/api/auth/me`);
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

// ── Admin user management (require an admin session; 403 otherwise) ────────────

export interface AdminUser {
  email: string;
  role: string;
  added_by: string | null;
  added_at: string | null;
  active: boolean;
  /** RAW allowed-apps config: null = default (all apps). */
  apps: string[] | null;
}

export async function adminListUsers(): Promise<AdminUser[]> {
  const res = await fetch(`${API_BASE}/api/admin/users`);
  if (!res.ok) throw new Error(await extractError(res));
  const body = await res.json();
  return body.users;
}

export async function adminAddUser(
  email: string, role: string,
): Promise<{ ok: boolean; email: string; role: string }> {
  const res = await fetch(`${API_BASE}/api/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

export async function adminSetRole(email: string, role: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(email)}/role`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

export async function adminSetApps(email: string, apps: string[]): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(email)}/apps`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apps }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

export async function adminRemoveUser(email: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

/** Public Google sign-in config (whether enabled, the public client id, the
 *  allowed email domain). Returns disabled config on any failure. */
export async function getGoogleConfig(): Promise<{ enabled: boolean; clientId: string; domain: string }> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/google-config`);
    if (!res.ok) return { enabled: false, clientId: '', domain: '' };
    return await res.json();
  } catch {
    return { enabled: false, clientId: '', domain: '' };
  }
}

/** Exchange a Google ID-token credential for a session. Backend verifies the
 *  token and enforces the @domain restriction; throws with the reason on 401/403. */
export async function googleSignIn(
  credential: string,
): Promise<{ mapper: Mapper; token: string; email: string }> {
  const res = await fetch(`${API_BASE}/api/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  if (!res.ok) throw new Error(await extractError(res));
  const body = await res.json();
  return {
    mapper: { id: body.id, display_name: body.display_name, role: body.role, apps: body.apps },
    token: body.token,
    email: body.email,
  };
}
